import * as THREE from 'three'
import {
  areCompiledClipStatesEqual,
  COMPILED_CLIP_EPSILON,
  COMPILED_CLIP_HIGHLIGHT_COLOR,
  type CompiledClipBox,
  type CompiledClipEvaluation,
  type CompiledClipPolygon,
  type CompiledClipState,
  type CompiledClipVolume,
  CompiledClipVolumeKind,
  createCompiledClipState,
  evaluateCompiledClipState,
  isPointInCompiledClipBox,
  isPointInCompiledClipPolygon,
  isPointInCompiledClipVolume,
  MAX_COMPILED_CLIP_GROUPS,
  MAX_COMPILED_CLIP_VOLUMES,
} from '@/shared/clipping/CompiledClipState'
import { ClipPolygonMaxVerticeCount } from '@/shared/constants'
import { getLoggerManager } from '@/shared/logging'
import type { ClipVolume } from '@/shared/types/core'

const log = getLoggerManager().getLogger('gsplat:clip')

/** 同时参与 GSplat shader 判定的裁切体总数。 */
export const GSPLAT_MAX_CLIP_VOLUMES = MAX_COMPILED_CLIP_VOLUMES

/** @deprecated 使用 GSPLAT_MAX_CLIP_VOLUMES；保留别名避免内部调用方一次性迁移。 */
export const GSPLAT_MAX_CLIP_POLYGONS = GSPLAT_MAX_CLIP_VOLUMES

/** volume group 数量与现有 polygon 分组语义保持一致。 */
export const GSPLAT_MAX_CLIP_GROUPS = MAX_COMPILED_CLIP_GROUPS

/** CPU/GLSL 边界判定使用的统一容差。 */
export const GSPLAT_CLIP_EPSILON = COMPILED_CLIP_EPSILON

/** 每个 volume 行头占用的 texel 数量：mat4(4) + bounds(1) + metadata(1)。 */
export const GSPLAT_CLIP_HEADER_TEXELS = 6

/** 每个 volume 在 DataTexture 中占用一行。 */
export const GSPLAT_CLIP_TEXTURE_WIDTH = GSPLAT_CLIP_HEADER_TEXELS + ClipPolygonMaxVerticeCount

/** 预览高亮色与点云 polygon 裁切保持一致。 */
export const GSPLAT_CLIP_HIGHLIGHT_COLOR = COMPILED_CLIP_HIGHLIGHT_COLOR

/** 保留现有 GSplat 命名的共享裁切类型别名。 */
export { CompiledClipVolumeKind as GSplatClipVolumeKind }
export type GSplatClipPolygon = CompiledClipPolygon
export type GSplatClipBox = CompiledClipBox
export type GSplatClipVolume = CompiledClipVolume
export type GSplatClipState = CompiledClipState
export type GSplatClipEvaluation = CompiledClipEvaluation

/** GSplat 裁切快照针对单个资产世界矩阵编译得到的 GPU 数据包。 */
export interface GSplatClipPacket {
  readonly texture: THREE.DataTexture
  /** 通用裁切体数量。 */
  volumeCount?: number
  /** @deprecated 当前字段值同 volumeCount，仅为兼容现有材质和测试。 */
  polygonCount: number
  /** 当前 packet 中 polygon 的最大顶点数；box-only packet 为 0。 */
  maxPolygonVertices: number
  sourceVersion: number
  readonly assetMatrixWorld: THREE.Matrix4
}

/** 判断 ClipVolume 是否属于 GSplat 支持的全局 polygon 范围。 */
export function isGlobalGSplatPolygonVolume(volume: ClipVolume): boolean {
  return volume.type === 'polygon' && volume.viewId == null
}

/** 判断 ClipVolume 是否属于 GSplat 支持的全局 Box 范围。 */
export function isGlobalGSplatBoxVolume(volume: ClipVolume): boolean {
  return volume.type === 'box' && volume.viewId == null
}

/** 判断 ClipVolume 是否可能由 GSplat 编译器消费。 */
export function isGlobalGSplatClipVolume(volume: ClipVolume): boolean {
  return isGlobalGSplatPolygonVolume(volume) || isGlobalGSplatBoxVolume(volume)
}

/** 将全局 ClipService volume 编译为 GSplat 裁切快照。 */
export function createGSplatClipState(inputVolumes: ReadonlyArray<ClipVolume>): GSplatClipState {
  const state = createCompiledClipState(inputVolumes)
  const droppedCount = state.droppedCount ?? 0
  if (droppedCount > 0) {
    log.warn(
      `忽略无效或超容量的 GSplat 裁切体，数量: ${droppedCount}, ` +
        `生效数量: ${state.volumes.length}`
    )
  }
  return state
}

function writeMatrixTexels(target: Float32Array, rowOffset: number, matrix: THREE.Matrix4): void {
  const elements = matrix.elements
  for (let index = 0; index < 16; index += 1) {
    target[rowOffset + index] = elements[index]!
  }
}

function createClipTexture(data: Float32Array): THREE.DataTexture {
  const texture = new THREE.DataTexture(
    data,
    GSPLAT_CLIP_TEXTURE_WIDTH,
    GSPLAT_MAX_CLIP_VOLUMES,
    THREE.RGBAFormat,
    THREE.FloatType
  )
  texture.name = 'GSplatClipData'
  texture.minFilter = THREE.NearestFilter
  texture.magFilter = THREE.NearestFilter
  texture.generateMipmaps = false
  texture.flipY = false
  texture.colorSpace = THREE.NoColorSpace
  texture.needsUpdate = true
  return texture
}

function getWorldToClipMatrix(volume: GSplatClipVolume): THREE.Matrix4 {
  return volume.kind === CompiledClipVolumeKind.POLYGON
    ? volume.worldViewProjection
    : volume.worldToBox
}

function writeAssetMatrices(
  target: Float32Array,
  state: GSplatClipState,
  assetMatrixWorld: THREE.Matrix4
): void {
  for (let volumeIndex = 0; volumeIndex < state.volumes.length; volumeIndex += 1) {
    const volume = state.volumes[volumeIndex]!
    const rowOffset = volumeIndex * GSPLAT_CLIP_TEXTURE_WIDTH * 4
    const assetToClipMatrix = getWorldToClipMatrix(volume).clone().multiply(assetMatrixWorld)
    writeMatrixTexels(target, rowOffset, assetToClipMatrix)
  }
}

function writePacketData(
  target: Float32Array,
  state: GSplatClipState,
  assetMatrixWorld: THREE.Matrix4
): void {
  target.fill(0)
  writeAssetMatrices(target, state, assetMatrixWorld)

  for (let volumeIndex = 0; volumeIndex < state.volumes.length; volumeIndex += 1) {
    const volume = state.volumes[volumeIndex]!
    const rowOffset = volumeIndex * GSPLAT_CLIP_TEXTURE_WIDTH * 4
    const boundsOffset = rowOffset + 4 * 4
    const metadataOffset = rowOffset + 5 * 4

    if (volume.kind === CompiledClipVolumeKind.POLYGON) {
      target[boundsOffset] = volume.aabb.x
      target[boundsOffset + 1] = volume.aabb.y
      target[boundsOffset + 2] = volume.aabb.z
      target[boundsOffset + 3] = volume.aabb.w
      target[metadataOffset] = volume.vertices.length
      for (let vertexIndex = 0; vertexIndex < volume.vertices.length; vertexIndex += 1) {
        const vertex = volume.vertices[vertexIndex]!
        const vertexOffset = rowOffset + (GSPLAT_CLIP_HEADER_TEXELS + vertexIndex) * 4
        target[vertexOffset] = vertex.x
        target[vertexOffset + 1] = vertex.y
      }
    } else {
      target[boundsOffset] = -0.5
      target[boundsOffset + 1] = -0.5
      target[boundsOffset + 2] = 0.5
      target[boundsOffset + 3] = 0.5
    }

    target[metadataOffset + 1] = volume.clipTask
    target[metadataOffset + 2] = volume.groupIndex
    target[metadataOffset + 3] = volume.kind
  }
}

/** 为指定 unified mesh 的 asset-local 坐标编译 GPU packet。 */
export function createGSplatClipPacket(
  state: GSplatClipState,
  assetMatrixWorld: THREE.Matrix4
): GSplatClipPacket | null {
  if (state.volumes.length === 0) {
    return null
  }

  const data = new Float32Array(GSPLAT_CLIP_TEXTURE_WIDTH * GSPLAT_MAX_CLIP_VOLUMES * 4)
  writePacketData(data, state, assetMatrixWorld)

  return {
    texture: createClipTexture(data),
    volumeCount: state.volumes.length,
    polygonCount: state.volumes.length,
    maxPolygonVertices: state.polygons.reduce(
      (maximum, polygon) => Math.max(maximum, polygon.vertices.length),
      0
    ),
    sourceVersion: state.version,
    assetMatrixWorld: assetMatrixWorld.clone(),
  }
}

/** 创建零 volume 的常驻 packet，用于提前编译裁切 shader 变体。 */
export function createEmptyGSplatClipPacket(assetMatrixWorld: THREE.Matrix4): GSplatClipPacket {
  return {
    texture: createClipTexture(
      new Float32Array(GSPLAT_CLIP_TEXTURE_WIDTH * GSPLAT_MAX_CLIP_VOLUMES * 4)
    ),
    volumeCount: 0,
    polygonCount: 0,
    maxPolygonVertices: 0,
    sourceVersion: 0,
    assetMatrixWorld: assetMatrixWorld.clone(),
  }
}

/** 原地更新 packet，保持 texture identity，避免材质/投影 pipeline 因同类裁切切换而重建。 */
export function updateGSplatClipPacket(
  packet: GSplatClipPacket,
  state: GSplatClipState,
  assetMatrixWorld: THREE.Matrix4
): void {
  const data = packet.texture.image.data as Float32Array
  writePacketData(data, state, assetMatrixWorld)
  packet.volumeCount = state.volumes.length
  packet.polygonCount = state.volumes.length
  packet.maxPolygonVertices = state.polygons.reduce(
    (maximum, polygon) => Math.max(maximum, polygon.vertices.length),
    0
  )
  packet.sourceVersion = state.version
  packet.assetMatrixWorld.copy(assetMatrixWorld)
  packet.texture.needsUpdate = true
}

/** 原地更新 packet 的资产世界矩阵，避免重新分配和销毁 DataTexture。 */
export function updateGSplatClipPacketMatrix(
  packet: GSplatClipPacket,
  state: GSplatClipState,
  assetMatrixWorld: THREE.Matrix4
): void {
  const data = packet.texture.image.data as Float32Array
  writeAssetMatrices(data, state, assetMatrixWorld)
  packet.assetMatrixWorld.copy(assetMatrixWorld)
  packet.texture.needsUpdate = true
}

/** 深拷贝裁切 packet，使克隆材质独立拥有对应 DataTexture。 */
export function cloneGSplatClipPacket(packet: GSplatClipPacket): GSplatClipPacket {
  const data = packet.texture.image.data as Float32Array
  return {
    texture: createClipTexture(new Float32Array(data)),
    volumeCount: packet.volumeCount ?? packet.polygonCount,
    polygonCount: packet.polygonCount,
    maxPolygonVertices: packet.maxPolygonVertices,
    sourceVersion: packet.sourceVersion,
    assetMatrixWorld: packet.assetMatrixWorld.clone(),
  }
}

/** 判断两个已编译裁切快照的有效内容是否完全相同。 */
export function areGSplatClipStatesEqual(left: GSplatClipState, right: GSplatClipState): boolean {
  return areCompiledClipStatesEqual(left, right)
}

/** 判断 NDC 点是否位于指定 GSplat 裁切多边形内部或边界容差内。 */
export function isPointInGSplatClipPolygon(
  point: THREE.Vector2,
  polygon: GSplatClipPolygon
): boolean {
  return isPointInCompiledClipPolygon(point, polygon)
}

/** 判断世界坐标点是否位于指定 GSplat Box 内部或边界容差内。 */
export function isPointInGSplatClipBox(point: THREE.Vector3, box: GSplatClipBox): boolean {
  return isPointInCompiledClipBox(point, box)
}

/** 判断世界坐标点是否命中单个已编译裁切体。 */
export function isPointInGSplatClipVolume(point: THREE.Vector3, volume: GSplatClipVolume): boolean {
  return isPointInCompiledClipVolume(point, volume)
}

/** 按统一 group/task 语义计算世界坐标中心点的隐藏与高亮结果。 */
export function evaluateGSplatClipState(
  state: GSplatClipState,
  worldPoint: THREE.Vector3
): GSplatClipEvaluation {
  return evaluateCompiledClipState(state, worldPoint)
}

/** 判断世界坐标 splat 中心在当前裁切快照下是否可见。 */
export function isGSplatCenterVisible(state: GSplatClipState, worldPoint: THREE.Vector3): boolean {
  return !evaluateGSplatClipState(state, worldPoint).hidden
}
